import { logger } from '../utils/logger';

interface BrowserAuthTransportConfig {
  getCsrfToken: () => string | null;
  refreshSession?: () => Promise<string | null>;
  onUnauthorized: () => void;
}

let activeConfig: BrowserAuthTransportConfig | null = null;

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const configureBrowserAuthTransport = (
  config: BrowserAuthTransportConfig
): (() => void) => {
  activeConfig = config;

  return () => {
    if (activeConfig === config) {
      activeConfig = null;
    }
  };
};

export const getBrowserAuthHeaders = (method?: string): Record<string, string> => {
  if (!method || !MUTATING_METHODS.has(method.toUpperCase())) {
    return {};
  }

  const csrfToken = activeConfig?.getCsrfToken();
  return csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
};

export const notifyBrowserAuthUnauthorized = (): void => {
  activeConfig?.onUnauthorized();
};

export const refreshBrowserAuthSession = async (): Promise<string | null> => {
  return activeConfig?.refreshSession?.() ?? null;
};

const PORTAL_REDIRECT_DIAGNOSTIC_KEY = 'sg-coach-portal-redirect-diagnostic';

export interface PortalRedirectDiagnostic {
  source: 'retry-redirect' | 'explicit-logout';
  url: string;
  hasRedirectTo: boolean;
  timestamp: number;
}

/**
 * Persists a record of which redirect path sent the browser to the Portal.
 * Console output doesn't survive the cross-origin navigation, so this uses
 * localStorage (not sessionStorage) to survive the full round trip and is
 * read back and logged the next time the app boots.
 */
export const recordPortalRedirectDiagnostic = (
  source: PortalRedirectDiagnostic['source'],
  url: string,
): void => {
  if (!logger.isDev()) {
    return;
  }

  try {
    const diagnostic: PortalRedirectDiagnostic = {
      source,
      url,
      hasRedirectTo: new URL(url).searchParams.has('redirect_to'),
      timestamp: Date.now(),
    };
    window.localStorage.setItem(PORTAL_REDIRECT_DIAGNOSTIC_KEY, JSON.stringify(diagnostic));
  } catch {
    // localStorage can be unavailable in restricted browser contexts.
  }
};

export const consumePortalRedirectDiagnostic = (): PortalRedirectDiagnostic | null => {
  if (!logger.isDev()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(PORTAL_REDIRECT_DIAGNOSTIC_KEY);
    if (!raw) {
      return null;
    }
    window.localStorage.removeItem(PORTAL_REDIRECT_DIAGNOSTIC_KEY);
    return JSON.parse(raw) as PortalRedirectDiagnostic;
  } catch {
    return null;
  }
};
